import ExternalLink from '@/components/ExternalLink'
import CustomerMktIcons from '@/icons/CustomerMktIcons'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { IPermissionProps } from '@/types/shared'
import { formatCurrency } from '@/utils/function'
import { paths } from '@/utils/path'
import useAuthStore from '@/stores/useAuthStore'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import {
    getContactSourceByID,
    getRevenueContactSourceByCustomer,
    updateContactSource,
} from '../../services'
import { ContactSourceUpdatePayload } from '../../types/payload'
import {
    ContactChanel,
    Industries,
    IndustryOptions,
    Services,
} from '../../types/utils'
import ContactConversation from './ContactConversation'
import LivechatConversationPreview from './LivechatConversationPreview'
import LeadConversionFlowCompact from './LeadConversionFlowCompact'

// UUID pattern to detect internal livechat room IDs stored in contact_source_profile
const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const Detail: React.FC<IPermissionProps> = () => {
    const { contactSourceID } = useParams<{ contactSourceID: string }>()
    const { t } = useTranslation()
    const { t: tMar } = useTranslation('marketing')
    const { employee } = useAuthStore()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'contact-source-list',
    })
    const {
        data: contactSource,
        renderState,
        refetch: callbackContactSource,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTACT_SOURCE_DETAIL, contactSourceID],
            queryFn: async () => {
                const result = await getContactSourceByID({
                    contact_source_id: Number(contactSourceID) || 0,
                })
                return result
            },
        },
        {
            errorDisplayMode: 'page',
        }
    )

    // Fetch revenue data
    const { data: revenueData } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTACT_SOURCE_REVENUE, contactSourceID],
            queryFn: async () => {
                const result = await getRevenueContactSourceByCustomer({
                    contact_source_id: Number(contactSourceID) || 0,
                })
                return result
            },
            enabled: !!contactSourceID,
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const handleEdit = async (values: ContactSourceUpdatePayload) => {
        console.log('values', values)
        await updateContactSource({
            contact_source_id: Number(contactSourceID),
            contact_source_name: values.contact_source_name,
            contact_source_email: values.contact_source_email,
            contact_source_type: values.contact_source_type,
            contact_source_channel: values.contact_source_channel,
            contact_source_company: values.contact_source_company,
            contact_source_messages: values.contact_source_messages,
            contact_source_assignee_ids: values.contact_source_assignee_ids,
            contact_source_phone: values.contact_source_phone,
            contact_source_website: values.contact_source_website,
            contact_source_notes: values.contact_source_notes,
            contact_source_status: values.contact_source_status,
            contact_source_position: values.contact_source_position,
            contact_source_profile: values.contact_source_profile,
            contact_source_industry: values.contact_source_industry,
            contact_source_interested_services:
                values.contact_source_interested_services,
        })
    }

    const { mutateAsync: updateContactSourceMutation } = useMutationWithHandler(
        {
            mutationFn: handleEdit,
        },
        {
            reactQueryKeys: QUERY_KEYS.MARKETING,
            successMessage: t('updated-success'),
            onSuccess: () => {
                callbackContactSource()
                refetchCurrent()
            },
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const channel = contactSource?.result?.contact_source_channel
    const isLiveChat =
        channel === 'livechat' ||
        channel === 'LIVE_CHAT' ||
        channel === 'fb_messenger' ||
        channel === 'FB_MESSENGER'

    // Detect internal livechat leads
    // contact_source_object_id stores room UUID, contact_source_object_type = 'Internal-Livechat'
    const objectType = contactSource?.result?.contact_source_object_type
    const objectId = String(
        contactSource?.result?.contact_source_object_id || ''
    )
    const isInternalLivechat =
        objectType === 'Internal-Livechat' ||
        (isLiveChat && UUID_REGEX.test(objectId))
    const livechatRoomId = isInternalLivechat ? objectId : ''

    // Check if current employee can edit
    // If lead has assignees and current employee is NOT in the list, disable edit
    const assigneeIds = contactSource?.result?.contact_source_assignee_ids || []
    const hasAssignees = assigneeIds.length > 0
    const isCurrentUserAssignee = hasAssignees
        ? assigneeIds.includes(employee?.employee_id ?? -1)
        : true // If no assignees, anyone can edit
    const canEdit = isCurrentUserAssignee
    const isAlreadyConverted = contactSource?.result?.converted ?? false

    // Get revenue safely
    const revenue =
        revenueData && 'result' in revenueData
            ? revenueData.result?.revenue
            : null

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.customerMkt,
                        title: t('contact-source-list'),
                    },
                    {
                        path: `${paths.customerMkt}/${contactSource?.result?.contact_source_id}`,
                        title: t('customer-contact-information'),
                    },
                ]}
            />

            {/* Warning if user cannot edit */}
            {!canEdit && hasAssignees && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 flex items-center gap-2">
                    <span className="text-amber-600 text-sm">
                        {t('cannot-edit-not-assignee')}
                    </span>
                </div>
            )}

            <GridInformationWithHookForm
                defaultFormValues={{
                    contact_source_name:
                        contactSource?.result?.contact_source_name || '',
                    contact_source_position:
                        contactSource?.result?.contact_source_position || '',
                    contact_source_phone:
                        contactSource?.result?.contact_source_phone || '',
                    contact_source_email:
                        contactSource?.result?.contact_source_email || '',
                    contact_source_company:
                        contactSource?.result?.contact_source_company || '',
                    contact_source_website:
                        contactSource?.result?.contact_source_website || '',
                    contact_source_profile:
                        contactSource?.result?.contact_source_profile || '',
                    contact_source_industry:
                        contactSource?.result?.contact_source_industry || '',
                    contact_source_channel:
                        contactSource?.result?.contact_source_channel || '',
                    contact_source_interested_services:
                        (typeof contactSource?.result
                            ?.contact_source_interested_services === 'string'
                            ? JSON.parse(
                                  contactSource.result
                                      .contact_source_interested_services
                              )
                            : contactSource?.result
                                  ?.contact_source_interested_services) || [],
                    contact_source_status:
                        contactSource?.result?.contact_source_status || '',
                    contact_source_notes:
                        contactSource?.result?.contact_source_notes || '',
                    contact_source_assignee_ids:
                        contactSource?.result?.contact_source_assignee_ids ||
                        [],
                }}
                onEdit={
                    canEdit ? (updateContactSourceMutation as any) : undefined
                }
                data={[
                    {
                        label: t('contact-source-name'),
                        value: contactSource?.result?.contact_source_name,
                        formItemProps: {
                            name: 'contact_source_name',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('position'),
                        value: contactSource?.result?.contact_source_position,
                        formItemProps: {
                            name: 'contact_source_position',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('contact-source-phone'),
                        value: contactSource?.result?.contact_source_phone,
                        formItemProps: {
                            name: 'contact_source_phone',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('email'),
                        value: contactSource?.result?.contact_source_email,
                        formItemProps: {
                            name: 'contact_source_email',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('company'),
                        value: contactSource?.result?.contact_source_company,
                        formItemProps: {
                            name: 'contact_source_company',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('Website'),
                        value: contactSource?.result?.contact_source_website ? (
                            <ExternalLink
                                link={
                                    contactSource?.result
                                        ?.contact_source_website
                                }
                            >
                                {contactSource?.result?.contact_source_website}
                            </ExternalLink>
                        ) : (
                            '---'
                        ),
                        formItemProps: {
                            name: 'contact_source_website',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('website-referrer'),
                        value: contactSource?.result?.website_referrer || '---',
                    },
                    {
                        label: t('source-url'),
                        value: contactSource?.result?.metadata?.source_url ? (
                            <ExternalLink
                                link={
                                    contactSource?.result?.metadata?.source_url
                                }
                            >
                                {contactSource?.result?.metadata?.source_url}
                            </ExternalLink>
                        ) : (
                            '---'
                        ),
                    },
                    {
                        label: t('contact-source-profile'),
                        value: contactSource?.result?.contact_source_profile?.match(
                            /(http|www)/
                        ) ? (
                            <a
                                href={
                                    contactSource?.result
                                        ?.contact_source_profile
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: 'underline',
                                    color: '#1e40af',
                                }}
                            >
                                {contactSource?.result?.contact_source_profile}
                            </a>
                        ) : (
                            contactSource?.result?.contact_source_profile
                        ),
                        formItemProps: {
                            name: 'contact_source_profile',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('contact-source-industry'),
                        value: Industries.find(
                            (item) =>
                                item.value ===
                                contactSource?.result?.contact_source_industry
                        )?.label,
                        formItemProps: {
                            name: 'contact_source_industry',
                            type: 'select',
                            options: IndustryOptions,
                        },
                    },
                    {
                        label: t('contact-source-channel'),
                        value: ContactChanel(tMar).find(
                            (item) =>
                                item.value ===
                                contactSource?.result?.contact_source_channel
                        )?.label,
                        formItemProps: {
                            name: 'contact_source_channel',
                            type: 'select',
                            options: ContactChanel(tMar),
                        },
                    },
                    {
                        label: t('contact-source-interested-services'),
                        value: Services.find((item) =>
                            contactSource?.result?.contact_source_interested_services?.includes(
                                item.value
                            )
                        )?.label,
                        formItemProps: {
                            name: 'contact_source_interested_services',
                            type: 'select',
                            options: Services,
                            multiple: true,
                        },
                    },
                    {
                        label: t('revenue'),
                        value:
                            revenue != null ? (
                                <span className="font-semibold text-green-600">
                                    {formatCurrency(revenue)}
                                </span>
                            ) : (
                                '---'
                            ),
                    },
                    {
                        label: t('note'),
                        value: contactSource?.result?.contact_source_notes,
                        formItemProps: {
                            name: 'contact_source_notes',
                            type: 'textfield',
                        },
                    },
                    {
                        label: tMar('contact-source-assignee'),
                        value:
                            contactSource?.result?.contact_source_assignee_name
                                ?.map((assignee) => assignee.employee_name)
                                ?.join(', ') || '',
                        renderEditComponent: (control) => (
                            <EmployeeSelect
                                control={control}
                                name="contact_source_assignee_ids"
                                multiple
                                showLabel={false}
                                label="marketing:contact-source-assignee"
                            />
                        ),
                    },
                    {
                        label: isLiveChat ? 'LIVE CHAT/ MESSENGERS' : '',
                        value: isInternalLivechat ? (
                            <div className="max-h-[500px] max-w-full overflow-y-auto">
                                <LivechatConversationPreview
                                    roomId={livechatRoomId}
                                />
                            </div>
                        ) : isLiveChat &&
                          contactSource?.result?.contact_source_object_id ? (
                            <div className="max-h-[500px] max-w-full overflow-y-auto">
                                <ContactConversation
                                    conversationId={Number(
                                        contactSource.result
                                            .contact_source_object_id
                                    )}
                                    assignee={
                                        (contactSource.result
                                            .contact_source_assignee as any) ??
                                        undefined
                                    }
                                />
                            </div>
                        ) : null,
                        className: 'lg:col-span-3',
                    },
                ]}
                head={{
                    title: t('customer-contact-information'),
                    icon: <CustomerMktIcons />,
                }}
            />

            {/* Lead Conversion Flow with Definitions */}
            <LeadConversionFlowCompact
                currentStatus={contactSource?.result?.contact_source_status}
                isConverted={isAlreadyConverted}
                hasAssignees={hasAssignees}
            />
        </>
    )
}

export default Detail
